import { useSelector } from "react-redux";
import { RootState } from "../../../../store";
import { formMappings } from "../../../../config/formMappings";
import { FormEntry } from "../../../../types/types";

interface TotalTableCountProps {
  v2Entries: FormEntry[];
}

export default function TotalTableCount({ v2Entries }: TotalTableCountProps) {
  const entries = useSelector((state: RootState) => state.formEntries.entries);
  const activeFormName = useSelector(
    (state: RootState) => state.activeForm.activeFormName
  );
  const formMapping = formMappings[activeFormName as keyof typeof formMappings];
  const isVersion1 = formMapping?.version === "1";
  const bazaarTableMapping =
    formMapping && "bazaarTable" in formMapping
      ? formMapping.bazaarTable?.fieldMappings
      : undefined;

  // Calculate total tables needed based on version
  const totalTables = (isVersion1 ? entries : v2Entries)
    .filter((entry) => {
      if (isVersion1) {
        return entry.meta["Will you be needing a table for bazaar?"] === "Yes";
      } else {
        // For v2+, use registeredForConference field from bazaarTable mapping
        if (!bazaarTableMapping) return false;
        const registeredField =
          entry.meta[bazaarTableMapping.registeredForConference] ||
          entry.meta["Will you be needing a table for bazaar?"];
        if (registeredField !== "Yes") return false;

        // For v2+, also check if entry is paid
        const paidStatusRaw =
          entry.meta[bazaarTableMapping.paid] || entry.meta["Paid?"] || "No";
        const paidStatus = Array.isArray(paidStatusRaw)
          ? paidStatusRaw[0]
          : paidStatusRaw;
        return paidStatus === "Yes";
      }
    })
    .reduce((sum, entry) => {
      if (isVersion1) {
        const tablesStr =
          entry.meta["How many tables do you need? ($25/per table)"] || "0";
        const tables = parseInt(
          Array.isArray(tablesStr) ? tablesStr[0] : tablesStr,
          10
        );
        return sum + (isNaN(tables) ? 0 : tables);
      } else {
        // For v2+, use numberOfTables field from bazaarTable mapping
        if (!bazaarTableMapping) return sum;
        const tablesStr =
          entry.meta[bazaarTableMapping.numberOfTables] ||
          entry.meta["How many tables do you need? ($25/per table)"] ||
          "0";
        const tables = parseInt(
          Array.isArray(tablesStr) ? tablesStr[0] : tablesStr,
          10
        );
        return sum + (isNaN(tables) ? 0 : tables);
      }
    }, 0);

  return (
    <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
      <dt className="truncate text-sm font-medium text-gray-500">
        Tables Needed
      </dt>
      <dd className="mt-1 text-3xl font-semibold tracking-tight text-gray-900">
        {totalTables}
      </dd>
    </div>
  );
}
